import { Typography as FoodsTypography } from "@foodpilot/foods";
import { Stack, Typography as MuiTypography, useTheme } from "@mui/material";
import { NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";
import EmptySetIcon from "src/assets/icons/empty-set.svg?react";
import { useLanguage } from "src/context/LanguageProvider";
import { formatNumber } from "src/utils/formatting";

export type MainScoreProps = {
    appearance?: "legacy" | "product-v2";
    score: {
        value: NumericalScoreValue;
        unit: string | undefined;
    };
    referenceScore: number | undefined;
    precision: number;
};

export const MainScore = (props: MainScoreProps) => {
    const theme = useTheme();
    const { currentLanguage } = useLanguage();

    const { appearance = "legacy", score, referenceScore, precision } = props;

    const isProductV2 = appearance === "product-v2";
    const displayedScore = isProductV2 ? score.value : (score.value ?? referenceScore ?? null);
    const isScoreEmpty = displayedScore === null;
    const formattedScore =
        displayedScore === null ? null : (
            formatNumber(displayedScore, currentLanguage, {
                minimumFractionDigits: precision,
                maximumFractionDigits: precision,
            })
        );

    const scoreContent =
        isScoreEmpty ? <EmptySetIcon />
        : isProductV2 ? <FoodsTypography variant="number-big">{formattedScore}</FoodsTypography>
        : <MuiTypography variant="number-big">{formattedScore}</MuiTypography>;

    const unitContent =
        isProductV2 ?
            <FoodsTypography variant="body" sx={{ color: theme.custom.grey[2000] }}>
                {score.unit}
            </FoodsTypography>
        :   <MuiTypography
                variant="body"
                sx={{
                    color: theme.custom.grey[2000],
                    position: "relative",
                    top: theme.spacing(-1),
                }}
            >
                {score.unit}
            </MuiTypography>;

    if (!isProductV2) {
        return (
            <Stack
                data-testid="main-score-row"
                direction="row"
                flexBasis="100%"
                sx={{
                    alignItems: "flex-end",
                    gap: theme.spacing(1.25),
                }}
            >
                <Stack alignItems="center" gap={theme.spacing(1.5)}>
                    {isScoreEmpty ?
                        scoreContent
                    :   <Stack alignItems="center" gap={theme.spacing(1.25)}>
                            {scoreContent}
                        </Stack>
                    }
                </Stack>
                {unitContent}
            </Stack>
        );
    }

    return (
        <Stack
            alignItems={isScoreEmpty ? "center" : "baseline"}
            data-testid="main-score-row"
            direction="row"
            flexBasis="100%"
            gap={theme.spacing(1)}
        >
            {scoreContent}
            {unitContent}
        </Stack>
    );
};
